import { Container, Graphics, Text, TextStyle } from 'pixi.js';

export enum PowerUpType {
    SPEED_BOOST = 'speedBoost',
    EXTRA_JUMPS = 'extraJumps',
    MAGNET = 'magnet',
    SCORE_MULTIPLIER = 'scoreMultiplier',
}

interface ActivePowerUp {
    type: PowerUpType;
    endTime: number;
    duration: number;
}

export default class PowerUpSystem {
    private activePowerUps: Map<PowerUpType, ActivePowerUp> = new Map();
    private powerUpContainer: Container;
    private powerUpTexts: Map<PowerUpType, Text> = new Map();
    private readonly POWER_UP_DURATION = 60000; // 1 minute in milliseconds

    constructor(gameWorld: Container) {
        this.powerUpContainer = new Container();
        gameWorld.addChild(this.powerUpContainer);
        this.setupPowerUpDisplay();
    }

    private setupPowerUpDisplay(): void {
        // Create text displays for each power-up type
        const powerUpTypes = [
            PowerUpType.SPEED_BOOST,
            PowerUpType.EXTRA_JUMPS,
            PowerUpType.MAGNET,
            PowerUpType.SCORE_MULTIPLIER
        ];

        powerUpTypes.forEach((type, index) => {
            const text = new Text({
                text: '',
                style: new TextStyle({
                    fontFamily: 'Arial',
                    fontSize: 16,
                    fill: 0xFFFF00,
                    stroke: { color: 0x000000, width: 2 },
                    fontWeight: 'bold'
                })
            });
            text.x = 20;
            text.y = 100 + index * 30;
            text.visible = false;
            this.powerUpContainer.addChild(text);
            this.powerUpTexts.set(type, text);
        });
    }

    public activatePowerUp(type: PowerUpType): void {
        const now = Date.now();
        const existing = this.activePowerUps.get(type);
        
        // If already active, extend duration (stack up to 2 minutes max)
        if (existing) {
            const newEndTime = Math.min(existing.endTime + this.POWER_UP_DURATION, now + this.POWER_UP_DURATION * 2);
            this.activePowerUps.set(type, {
                type,
                endTime: newEndTime,
                duration: this.POWER_UP_DURATION
            });
        } else {
            this.activePowerUps.set(type, {
                type,
                endTime: now + this.POWER_UP_DURATION,
                duration: this.POWER_UP_DURATION
            });
        }
    }

    public isPowerUpActive(type: PowerUpType): boolean {
        const powerUp = this.activePowerUps.get(type);
        if (!powerUp) return false;
        
        const now = Date.now();
        if (now > powerUp.endTime) {
            this.activePowerUps.delete(type);
            return false;
        }
        return true;
    }

    public getPowerUpRemainingTime(type: PowerUpType): number {
        const powerUp = this.activePowerUps.get(type);
        if (!powerUp) return 0;
        
        const now = Date.now();
        return Math.max(0, powerUp.endTime - now);
    }

    public getSpeedMultiplier(): number {
        return this.isPowerUpActive(PowerUpType.SPEED_BOOST) ? 1.5 : 1.0;
    }

    public getExtraJumps(): number {
        return this.isPowerUpActive(PowerUpType.EXTRA_JUMPS) ? 2 : 0;
    }

    public getScoreMultiplier(): number {
        return this.isPowerUpActive(PowerUpType.SCORE_MULTIPLIER) ? 2.0 : 1.0;
    }

    public hasMagnet(): boolean {
        return this.isPowerUpActive(PowerUpType.MAGNET);
    }

    public update(delta: number): void {
        const now = Date.now();
        
        // Clean up expired power-ups
        for (const [type, powerUp] of this.activePowerUps.entries()) {
            if (now > powerUp.endTime) {
                this.activePowerUps.delete(type);
                const text = this.powerUpTexts.get(type);
                if (text) text.visible = false;
            }
        }

        // Update display
        this.updatePowerUpDisplay();
    }

    private updatePowerUpDisplay(): void {
        const powerUpNames = {
            [PowerUpType.SPEED_BOOST]: '⚡ SPEED BOOST',
            [PowerUpType.EXTRA_JUMPS]: '🦘 EXTRA JUMPS',
            [PowerUpType.MAGNET]: '🧲 MAGNET',
            [PowerUpType.SCORE_MULTIPLIER]: '⭐ 2x SCORE'
        };

        for (const [type, powerUp] of this.activePowerUps.entries()) {
            const text = this.powerUpTexts.get(type);
            if (text) {
                const remaining = Math.ceil(this.getPowerUpRemainingTime(type) / 1000);
                text.text = `${powerUpNames[type]} (${remaining}s)`;
                text.visible = true;
                
                // Pulse effect
                const pulse = 1 + Math.sin(Date.now() * 0.005) * 0.1;
                text.scale.set(pulse);
            }
        }
    }

    public clearAll(): void {
        this.activePowerUps.clear();
        this.powerUpTexts.forEach(text => text.visible = false);
    }
}

